/**
 * useBuddyNarration — drives the Sherlock buddy during intake wizard work.
 *
 * Derives `status` + `phase` + `coverage` from the wizard state machine and
 * dispatches `sherlock:buddy-context` `{kind:'intake', …}` events so the
 * global DetectiveFooter walks/talks during LLM-bound steps.
 *
 * Mapping (wizard state → buddy event):
 *   starting                          → running · start
 *   stepRunning (any phase) / extract → running · start    (fallback)
 *   stepRunning + plan|recommend      → running · gathering (+ coverage)
 *   stepRunning + synthesize          → running · drafting
 *   deduping                          → running · checking
 *   confirming                        → running · saving
 *   answering && !stepRunning         → paused  (round ready, user's turn)
 *   preview || done                   → done    (celebrate)
 *   error set / step_failed           → failed
 *
 * Works for BOTH the streaming path (rich per-step phases from SSE events)
 * AND the REST path (coarse: running·start while POST is in flight, then done).
 *
 * Dispatches `paused` on unmount so leaving /requests/new stops the buddy.
 */

import { useEffect, useRef } from 'react';
import type { IntakePhase } from '@/lib/buddyMessages';
import type { WizardState } from '@/hooks/useIntakeWizard';

/** Shape of the `sherlock:buddy-context` intake detail. */
interface IntakeBuddyDetail {
  kind: 'intake';
  status: 'running' | 'paused' | 'done' | 'failed';
  phase: IntakePhase;
  coverage?: { covered: number; total: number };
  sessionId?: string;
}

function dispatch(detail: IntakeBuddyDetail): void {
  window.dispatchEvent(
    new CustomEvent('sherlock:buddy-context', { detail }),
  );
}

/**
 * Parse coverage from the coverageNote string produced by coverage_update SSE.
 * Format: "4 covered · 3 remaining"
 * Returns null when the note is absent or doesn't match.
 */
function parseCoverage(
  note: string | null,
): { covered: number; total: number } | undefined {
  if (!note) return undefined;
  // e.g. "4 covered · 3 remaining"
  const m = note.match(/^(\d+)\s+covered\s*·\s*(\d+)\s+remaining/);
  if (!m) return undefined;
  const covered = parseInt(m[1] ?? '0', 10);
  const remaining = parseInt(m[2] ?? '0', 10);
  return { covered, total: covered + remaining };
}

/**
 * Derive the intake buddy detail from the wizard state.
 * Returns null when the state is idle/rehydrating/rehydrateError (no event).
 */
function deriveDetail(
  state: WizardState,
  sessionId?: string,
): IntakeBuddyDetail | null {
  const sid = sessionId;

  // Terminal — celebrate
  if (state.status === 'done' || state.status === 'preview') {
    return { kind: 'intake', status: 'done', phase: 'saving', sessionId: sid };
  }

  // Failed — reassure
  if (state.error != null && state.status !== 'answering') {
    // errors during answering+!stepRunning are in-page; only go failed when
    // the step was actually running and something broke
    if (state.stepRunning || state.status === 'starting') {
      return {
        kind: 'intake',
        status: 'failed',
        phase: 'start',
        sessionId: sid,
      };
    }
  }
  // step_failed event sets error + clears stepRunning — treat as failed
  if (state.error != null && state.status === 'answering' && !state.stepRunning) {
    // inline error visible, buddy can be paused (user's turn to retry)
    return { kind: 'intake', status: 'paused', phase: 'gathering', sessionId: sid };
  }

  // Starting or running a step
  if (state.status === 'starting' || state.stepRunning) {
    // Derive phase from the last step kind if available
    const phase = derivePhase(state);
    return {
      kind: 'intake',
      status: 'running',
      phase,
      coverage: phase === 'gathering' ? parseCoverage(state.coverageNote) : undefined,
      sessionId: sid,
    };
  }

  // Deduping
  if (state.status === 'deduping') {
    return { kind: 'intake', status: 'running', phase: 'checking', sessionId: sid };
  }

  // Confirming
  if (state.status === 'confirming') {
    return { kind: 'intake', status: 'running', phase: 'saving', sessionId: sid };
  }

  // Answering, not running — round ready, user's turn
  if (state.status === 'answering' && !state.stepRunning) {
    return { kind: 'intake', status: 'paused', phase: 'gathering', sessionId: sid };
  }

  // idle / rehydrating / rehydrateError — nothing to say
  return null;
}

/**
 * Map the current wizard session's phase string to an IntakePhase bucket.
 * Falls back to 'start' for unknown phases.
 */
function derivePhase(state: WizardState): IntakePhase {
  const sessionPhase = state.session?.phase;

  // Phase strings from the backend session model
  switch (sessionPhase) {
    case 'extract':
      return 'start';
    case 'plan':
    case 'recommend':
    case 'grille': // grill / grilling
    case 'grilling':
      return 'gathering';
    case 'synthesize':
    case 'synthesis':
      return 'drafting';
    case 'dedupe':
    case 'deduplication':
      return 'checking';
    case 'confirm':
      return 'saving';
    default:
      // Fallback: infer from wizard status
      if (state.status === 'starting') return 'start';
      if (state.status === 'answering') return 'gathering';
      if (state.status === 'deduping') return 'checking';
      if (state.status === 'confirming') return 'saving';
      return 'start';
  }
}

/**
 * useBuddyNarration — call this inside IntakeWizardPage (or wherever
 * useIntakeWizard is rendered) to wire the buddy to the wizard state.
 *
 * @param state       Current WizardState from useIntakeWizard.
 * @param sessionId   The session id (state.session?.id), if available.
 */
export function useBuddyNarration(state: WizardState, sessionId?: string): void {
  // Track the last dispatched detail so we only fire on real changes.
  const lastDetailRef = useRef<IntakeBuddyDetail | null>(null);

  useEffect(() => {
    const detail = deriveDetail(state, sessionId);
    if (detail === null) return;

    // Skip if nothing meaningful changed (avoid chattering on every render).
    const last = lastDetailRef.current;
    if (
      last !== null &&
      last.status === detail.status &&
      last.phase === detail.phase &&
      last.coverage?.covered === detail.coverage?.covered &&
      last.coverage?.total === detail.coverage?.total
    ) {
      return;
    }

    lastDetailRef.current = detail;
    dispatch(detail);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.status,
    state.stepRunning,
    state.error,
    state.coverageNote,
    state.session?.phase,
    sessionId,
  ]);

  // Cleanup on unmount: stop the buddy from walking after the user navigates away.
  useEffect(() => {
    return () => {
      dispatch({
        kind: 'intake',
        status: 'paused',
        phase: 'gathering',
        sessionId,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentional empty deps — run cleanup once on unmount only
}
